// Daily site log — month view (V0.4.15): which days are logged, signed or missing, with rain, barge and hold-ups, and the
// month's figures. Tap a day to open it; "Log today" opens today.
import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { fijiToday, monthGrid, type DailyLog, type DayBuilding } from '../lib/daily';
import { Button } from '../ui/kit';
import { C3PageLoading } from '../ui/C3Loader';

const NONE = '00000000-0000-0000-0000-000000000000';
const W: Record<string, string> = { fine: 'Fine', cloud: 'Cloud', showers: 'Showers', heavy_rain: 'Heavy rain' };
const shiftMonth = (m: string, n: number) => {
  const d = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + n, 1));
  return d.toISOString().slice(0, 7);
};

export default function Daily() {
  const { siteId, can } = useApp();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const today = fijiToday();
  const month = /^\d{4}-\d{2}$/.test(sp.get('m') ?? '') ? sp.get('m')! : today.slice(0, 7);
  const sid = siteId ?? NONE;
  const from = `${month}-01`;
  const to = `${shiftMonth(month, 1)}-01`;
  const logs = useRows<DailyLog>(() => supabase.from('daily_logs').select('*').eq('site_id', sid).gte('log_date', from).lt('log_date', to).is('deleted_at', null), [sid, month], ['daily_logs']);
  const ids = logs.rows.map((l) => l.id);
  const rows = useRows<Pick<DayBuilding, 'id' | 'log_id' | 'hold_up' | 'eot_evidence'>>(() => supabase.from('daily_log_buildings').select('id, log_id, hold_up, eot_evidence').in('log_id', ids.length ? ids : [NONE]).is('deleted_at', null), [ids.join()], ['daily_log_buildings']);
  const pics = useRows<{ id: string; linked_id: string }>(() => supabase.from('files').select('id, linked_id').eq('linked_table', 'daily_log_buildings').in('linked_id', rows.rows.length ? rows.rows.map((r) => r.id) : [NONE]).is('deleted_at', null), [rows.rows.map((r) => r.id).join()]);
  const grid = useMemo(() => {
    const per = logs.rows.map((l) => {
      const rs = rows.rows.filter((r) => r.log_id === l.id);
      return { ...l, photos: pics.rows.filter((p) => rs.some((r) => r.id === p.linked_id)).length, holdUps: rs.filter((r) => r.hold_up).length + (l.hold_up && !rs.some((r) => r.hold_up) ? 1 : 0), eot: rs.filter((r) => r.hold_up && r.eot_evidence).length };
    });
    return monthGrid(month, per, today);
  }, [logs.rows, rows.rows, pics.rows, month, today]);
  const s = grid.summary;
  const lead = grid.days[0]?.dow ?? 0;

  return (
    <div className="dl-page">
      <div className="dl-head">
        <div>
          <h1>Daily site log</h1>
          <p className="muted">One page per working day — site photos and works done for each active building, a few quick answers and a voice note SWI AI turns into the summary. Signed off by the SWI site PM.</p>
        </div>
        {can('daily_log', 4) ? <Button kind="primary" onClick={() => nav(`/daily/${today}`)}>+ Log today</Button> : null}
      </div>
      {logs.loading ? (
        <C3PageLoading label="Loading the daily log" />
      ) : (
        <div className="wk-cols">
          <div className="dl-card dl-cal">
            <div className="wk-row" style={{ justifyContent: 'space-between' }}>
              <div className="wk-row">
                <Button className="dl-sq" aria-label="Previous month" onClick={() => setSp({ m: shiftMonth(month, -1) })}>‹</Button>
                <h2>{new Date(from + 'T00:00:00Z').toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</h2>
                <Button className="dl-sq" aria-label="Next month" disabled={month >= today.slice(0, 7)} onClick={() => setSp({ m: shiftMonth(month, 1) })}>›</Button>
              </div>
              <div className="wk-row small">
                {s.missing.length ? <span className="wk-badge bad">{s.missing.length} day{s.missing.length === 1 ? '' : 's'} not logged</span> : null}
                {s.drafts ? <span className="wk-badge warn">{s.drafts} draft{s.drafts === 1 ? '' : 's'}</span> : null}
              </div>
            </div>
            <div className="dl-grid-wrap">
              <div className="dl-grid">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <span key={d} className="dl-dow">{d}</span>)}
                {Array.from({ length: lead }, (_, i) => <span key={`b${i}`} />)}
                {grid.days.map((d) => {
                  const st = d.dow === 6 && !d.log ? 'off' : d.log?.status === 'signed' ? 'signed' : d.log ? 'draft' : d.date > today ? 'future' : d.date === today ? 'today' : 'missing';
                  return (
                    <Link key={d.date} to={`/daily/${d.date}`} className={`dl-day ${st}`} aria-label={`${d.date} ${st}`} onClick={(e) => d.date > today && e.preventDefault()}>
                      <span className="dl-dn"><b>{Number(d.date.slice(8))}</b><span className="dl-st">{{ off: 'No work', signed: 'Signed', draft: 'Draft', future: '', today: 'Today', missing: 'Not logged' }[st]}</span></span>
                      {d.log ? (
                        <>
                          <span className="muted small">{d.log.weather ? W[d.log.weather] : '—'}{d.photos ? ` · ${d.photos} photo${d.photos === 1 ? '' : 's'}` : ''}</span>
                          <span className="dl-flags">
                            {d.log.weather_stop_h ? <span className="dl-fl r">Rain {d.log.weather_stop_h} h</span> : null}
                            {d.holdUps ? <span className="dl-fl d">Hold-up</span> : null}
                            {d.log.barge ? <span className="dl-fl b">Barge</span> : null}
                          </span>
                        </>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="dl-card">
            <h2>This month{month === today.slice(0, 7) ? ' so far' : ''}</h2>
            <div className="dl-kv"><span>Working days logged</span><b>{s.logged} of {s.working}</b></div>
            <div className="dl-kv"><span>Rain days</span><b>{s.rainDays}</b></div>
            <div className="dl-kv"><span>Hours lost to weather</span><b>{s.hoursLost} h</b></div>
            <div className="dl-kv"><span>Barge days</span><b>{s.bargeDays}</b></div>
            <div className="dl-kv"><span>Staff boat transfers</span><b>{s.transfers}</b></div>
            <div className="dl-kv"><span>Site photos</span><b>{s.photos}</b></div>
            <div className="dl-kv"><span>Consultant visits</span><b>{s.visits}</b></div>
            <div className="dl-kv"><span>Hold-ups · kept as EOT evidence</span><b>{s.holdUps} · {s.eot}</b></div>
            {s.missing.length ? <p className="muted small">Not logged: {s.missing.map((d) => <Link key={d} to={`/daily/${d}`}>{Number(d.slice(8))} </Link>)}</p> : null}
          </div>
        </div>
      )}
    </div>
  );
}
